import { createPath } from 'history';
import { parse } from 'qs';
import { createElement, FC, Fragment, useContext, useState } from 'react';
import { Redirect } from 'react-router';

import { OrganizationDetailFragment } from '../../generated/graphql';
import mergeUrlQuery from '../../utility/mergeUrlQuery';
import ProjectTypeDetailDialog from '../component/compound/ProjectTypeDetailDialog';
import ProjectTypeDetailDialogForTrial from '../component/compound/ProjectTypeDetailDialogForTrial';
import ProjectTypesListDisplay, { NoResult } from '../component/compound/ProjectTypesList';
import { context as ConfigContext } from '../context/config';
import { context as DependencyContext } from '../context/dependency';
import { context as HistoryContext } from '../context/history';
import { context as StateContext } from '../context/state';
import { useClearRecoilState } from '../page/ProjectEditingPage/hooks/useClearRecoilState';
import useUpdateProject from '../page/ProjectEditingPage/hooks/useUpdateProject';

type Props = {
  className?: string;
};

const ProjectTypesList: FC<Props> = ({ className }) => {
  const dependency = useContext(DependencyContext);
  const history = useContext(HistoryContext);
  const config = useContext(ConfigContext);
  const state = useContext(StateContext);
  const { createDraftProject } = useUpdateProject();
  const clearRecoilState = useClearRecoilState();

  const [selectedProjectTypeId, setSelectedProjectTypeId] = useState<string | null>(null);

  const query = parse(state.location.search.slice(1));
  const organizationId: string | null = query.org || null;
  const productPricing = state.acct.productPricing;

  if (!organizationId) {
    let organization!: OrganizationDetailFragment;

    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else if (state.availableOrganizations.items.length >= 1) {
      organization = state.availableOrganizations.items[0];
    }

    if (organization) {
      return <Redirect to={createPath(mergeUrlQuery(history.location, { org: organization.id }))} />;
    }
  } else {
    if (!state.availableOrganizations.items.some((item) => item.id === organizationId)) {
      return <Redirect to="/project_types" />;
    }
  }

  const isTrial = !organizationId;
  const basicProjectTypeKey = ['project_type_jp_for_ad', 'project_type_us_for_ad', 'project_type_gl_for_ad'];
  const projectTypes = state.projectTypes.projectTypes;
  const projectTypesLoading = state.projectTypes.projectTypesFetching >= 1;

  const isPointRequired = state.lastAccessedOrganization
    ? typeof state.lastAccessedOrganization.pointBalance === 'number'
    : false;

  if (!projectTypesLoading && projectTypes.length === 0) {
    return (
      <NoResult
        onClickInquiryButton={() => {
          dependency.listProjectTypesUsecase.requestAnotherProjectType();
        }}
      />
    );
  }

  const dialogs = isTrial
    ? projectTypes.map((projectType) => (
        <ProjectTypeDetailDialogForTrial
          projectType={projectType}
          open={selectedProjectTypeId === projectType.id}
          onRequestClose={() => setSelectedProjectTypeId(null)}
          key={projectType.id}
          isPointRequired={isPointRequired}
          urltoId={config.ID_WEB_URL}
          language={state.language}
          productPricing={productPricing || undefined}
        />
      ))
    : projectTypes.map((projectType) => (
        <ProjectTypeDetailDialog
          projectType={projectType}
          open={selectedProjectTypeId === projectType.id}
          onAccept={() => {
            clearRecoilState();
            createDraftProject({
              organizationId,
              projectTypeKey: projectType.key,
            });
          }}
          onRequestClose={() => setSelectedProjectTypeId(null)}
          key={projectType.id}
          isPointRequired={isPointRequired}
          productPricing={productPricing || undefined}
        />
      ));

  return (
    <>
      <ProjectTypesListDisplay
        loading={projectTypesLoading}
        projectTypes={projectTypes}
        basicProjectTypeKey={basicProjectTypeKey}
        onSelect={(_, projectType) => setSelectedProjectTypeId(projectType.id)}
        className={className}
      />
      {dialogs}
    </>
  );
};

export default ProjectTypesList;
